import { useAmiStatus, useMeasuredLoading, useVoltageViolations } from '../../hooks/useAmiAnalytics.ts'
import { AboutAnalysis } from './AboutAnalysis.tsx'
import { MeasuredLoadingTable } from './MeasuredLoadingTable.tsx'
import { ModelComparisonPanel } from './ModelComparisonPanel.tsx'
import { VoltageViolationsTable } from './VoltageViolationsTable.tsx'

interface Props {
  selectedFeederId: number | null
  /** Avg-demand parameter from the dashboard slider — passed through to the
   *  measured-loading request so the estimated column, its header, and the
   *  chip tooltips all reflect the SAME number (B4). */
  avgDemandKw: number
  windowDays?: number
}

/** Plain-language labels for the AMI ETL run status enum
 *  (ami_etl_runs.status: success | error | skipped_lock). Unknown values
 *  render de-snake_cased, never as a raw token (UX B2). */
const ETL_STATUS_LABELS: Record<string, string> = {
  success: 'succeeded',
  error: 'failed',
  skipped_lock: 'skipped — previous run still running',
}

function etlStatusLabel(status: string | null): string {
  if (!status) return 'status unknown'
  return ETL_STATUS_LABELS[status] ?? status.replace(/_/g, ' ')
}

export function AmiMeasuredTab({ selectedFeederId, avgDemandKw, windowDays = 30 }: Props) {
  const { data: status, error: statusError } = useAmiStatus()
  // Feeder scoping (spec §3.4 item 6): the loading and violations tables
  // follow the dashboard's selected feeder, like ModelComparisonPanel does.
  const loading = useMeasuredLoading({
    window_days: windowDays,
    feeder_id: selectedFeederId ?? undefined,
    avg_demand_kw: avgDemandKw,
  })
  const violations = useVoltageViolations({
    window_days: windowDays,
    feeder_id: selectedFeederId ?? undefined,
  })

  return (
    <div className="space-y-4">
      <AboutAnalysis introKey="ami" />
      {/* As-of badge — green requires BOTH a successful run AND fresh data
          (spec §3.4: a success-dot on month-old data is misleading). */}
      <div className="flex items-center gap-2 text-xs">
        {statusError ? (
          <span className="text-red-500">Failed to load AMI ETL status.</span>
        ) : status?.data_available ? (
          <>
            <span className={`inline-block w-2 h-2 rounded-full ${
              status.last_run_status === 'success' && !status.is_stale
                ? 'bg-green-500'
                : 'bg-amber-500'
            }`} />
            <span className="text-gray-600">
              AMI data as of <b>{status.as_of}</b> · last ETL{' '}
              {etlStatusLabel(status.last_run_status)}
              {status.meters_unmatched > 0 &&
                ` · ${status.meters_unmatched} unmatched meters`}
            </span>
          </>
        ) : (
          <span className="text-amber-600">
            No AMI summary data yet — has the nightly ETL (Task Scheduler Job 4) run?
          </span>
        )}
      </div>

      {/* Staleness banner (spec §3.4): as_of beyond the server threshold. */}
      {status?.data_available && status.is_stale && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          AMI data is stale — as of {status.as_of}, older than the{' '}
          {status.staleness_hours_threshold}h freshness threshold. Measured
          values below may not reflect current conditions.
        </div>
      )}

      {loading.isLoading ? (
        <div className="text-xs text-gray-400">Loading measured loading…</div>
      ) : loading.error ? (
        <div className="text-xs text-red-500 p-3">
          Failed to load measured loading data.
        </div>
      ) : loading.data ? (
        <MeasuredLoadingTable transformers={loading.data.transformers} avgDemandKw={avgDemandKw} />
      ) : null}

      {violations.isLoading ? (
        <div className="text-xs text-gray-400">Loading violations…</div>
      ) : violations.error ? (
        <div className="text-xs text-red-500 p-3">
          Failed to load voltage violations.
        </div>
      ) : violations.data ? (
        <VoltageViolationsTable
          meters={violations.data.meters}
          windowDays={violations.data.window_days}
          totalViolationMeters={violations.data.total_violation_meters}
        />
      ) : null}

      {selectedFeederId ? (
        <ModelComparisonPanel feederId={selectedFeederId} />
      ) : (
        <div className="text-xs text-gray-400 bg-white rounded-lg border border-gray-200 p-3">
          Select a feeder from the Overview table to run the model-vs-measured
          voltage comparison.
        </div>
      )}
    </div>
  )
}
